import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Option,
  Select,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Typography,
} from "@mui/joy";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import EmailIcon from "@mui/icons-material/Email";
import SharepointFolderPicker from "./Modals/Integrations/SharepointFolderPicker";
import WorkspaceEmailSenders from "./WorkspaceEmailSenders";
import WorkspaceEmailTemplates from "./WorkspaceEmailTemplates";
import DisabledEmailOverlay from "./Organization/DisabledEmailOverlay";
import {
  AvailableIntegration,
  OrganizationIntegrationSubstrateItem,
  SharepointFolderSelection,
  WorkspaceIntegrationSelection,
} from "../common/types";
import { JsonSchemaObject } from "../types/integrationCatalog";
import { useListOrganizationIntegrationSubstrateItems } from "../services/api";

export interface IntegrationWidgetContext {
  workspaceId?: string;
  organizationId?: string | null;
  enabledModules?: string[];
  emailEnabled?: boolean;
  availableIntegration?: AvailableIntegration | null;
  selection?: WorkspaceIntegrationSelection | null;
  ensureSelection?: () => Promise<WorkspaceIntegrationSelection | null>;
}

export interface IntegrationWidgetProps {
  schema: JsonSchemaObject;
  value: Record<string, any>;
  onChange: (nextValue: Record<string, any>) => void;
  disabled?: boolean;
  fieldId: string;
  context?: IntegrationWidgetContext;
}

const setSharepointValue = (
  value: Record<string, any>,
  siteUrl: string,
  selectedFolders: SharepointFolderSelection[],
) => ({
  ...value,
  site_url: siteUrl,
  selected_folders: selectedFolders,
});

const SharePointSiteFolderPickerWidget: React.FC<IntegrationWidgetProps> = ({
  value,
  onChange,
  disabled,
  context,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [ensuringSelection, setEnsuringSelection] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [activeSelection, setActiveSelection] =
    useState<WorkspaceIntegrationSelection | null>(context?.selection || null);
  const [substrateItems, setSubstrateItems] = useState<
    OrganizationIntegrationSubstrateItem[]
  >([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const listSubstrateItems = useListOrganizationIntegrationSubstrateItems();
  const organizationId = context?.organizationId || null;
  const organizationIntegrationId = context?.availableIntegration?.id || null;
  const workspaceId = context?.workspaceId;

  useEffect(() => {
    const loadSubstrateItems = async () => {
      if (!organizationId || !organizationIntegrationId) {
        setSubstrateItems([]);
        return;
      }

      try {
        setItemsLoading(true);
        setItemsError(null);
        const response = await listSubstrateItems(
          organizationId,
          organizationIntegrationId,
          workspaceId,
        );
        setSubstrateItems(response);
      } catch (error: any) {
        const orgConfig = context?.availableIntegration?.org_config || {};
        const fallbackSiteUrls = Array.isArray(orgConfig.site_urls)
          ? (orgConfig.site_urls as string[])
          : [];
        setSubstrateItems(
          fallbackSiteUrls.map((siteUrl, index) => ({
            id: `legacy-site-${index}`,
            organization_integration_id: organizationIntegrationId,
            integration_key: "sharepoint",
            substrate_kind: "sharepoint_site",
            display_label: siteUrl,
            config: { site_url: siteUrl },
            created_at: "",
            updated_at: "",
          })),
        );
        setItemsError(
          error?.status === 404
            ? null
            : error?.message || "Failed to load SharePoint sites.",
        );
      } finally {
        setItemsLoading(false);
      }
    };

    loadSubstrateItems();
  }, [
    context?.availableIntegration?.org_config,
    listSubstrateItems,
    organizationId,
    organizationIntegrationId,
    workspaceId,
  ]);

  const siteOptions = substrateItems
    .map((item) => ({
      id: item.id,
      siteUrl:
        (typeof item.config?.site_url === "string" && item.config.site_url) ||
        item.display_label ||
        "",
      label:
        item.display_label ||
        (typeof item.config?.site_url === "string" ? item.config.site_url : ""),
    }))
    .filter((item) => item.siteUrl);
  const activeSiteOption =
    siteOptions.find((option) => option.id === value.substrate_item_id) ||
    siteOptions.find((option) => option.siteUrl === value.site_url) ||
    siteOptions[0];
  const siteUrl =
    (typeof activeSiteOption?.siteUrl === "string" &&
      activeSiteOption.siteUrl) ||
    "";
  const selectedFolders = Array.isArray(value.selected_folders)
    ? (value.selected_folders as SharepointFolderSelection[])
    : [];

  const ensureSelection = async () => {
    if (activeSelection) return activeSelection;
    if (!context?.ensureSelection) return null;

    try {
      setEnsuringSelection(true);
      setSelectionError(null);
      const createdSelection = await context.ensureSelection();
      setActiveSelection(createdSelection);
      return createdSelection;
    } catch (error: any) {
      setSelectionError(
        error?.message || "Failed to prepare the SharePoint selection.",
      );
      return null;
    } finally {
      setEnsuringSelection(false);
    }
  };

  const handleOpenPicker = async () => {
    if (!siteUrl) {
      setSelectionError(
        "No SharePoint site URLs are configured for this organization integration.",
      );
      return;
    }
    const selection = await ensureSelection();
    if (!selection) return;
    setPickerOpen(true);
  };

  return (
    <Stack spacing={1.5}>
      {itemsError && (
        <Alert color="warning" variant="soft">
          {itemsError}
        </Alert>
      )}
      {siteOptions.length === 0 ? (
        <Alert color="warning" variant="soft">
          Organization SharePoint settings do not expose any site URLs yet.
        </Alert>
      ) : (
        <Box>
          <Typography level="body-sm" sx={{ mb: 0.75 }}>
            SharePoint site
          </Typography>
          <Select
            value={activeSiteOption?.id || ""}
            onChange={(_, nextValue) => {
              const nextItem = siteOptions.find(
                (option) => option.id === String(nextValue || ""),
              );
              if (!nextItem) return;
              onChange({
                ...setSharepointValue(value, nextItem.siteUrl, []),
                substrate_item_id: nextItem.id,
              });
            }}
            disabled={disabled || ensuringSelection || itemsLoading}
          >
            {siteOptions.map((option) => (
              <Option key={option.id} value={option.id}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Box>
      )}

      {selectionError && (
        <Alert color="danger" variant="soft">
          {selectionError}
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Stack spacing={0.5}>
          <Typography level="body-sm">
            {selectedFolders.length > 0
              ? `${selectedFolders.length} folder${selectedFolders.length === 1 ? "" : "s"} selected`
              : "No folders selected"}
          </Typography>
          <Typography level="body-xs" color="neutral">
            If you leave folders empty, the integration falls back to the
            configured site scope.
          </Typography>
        </Stack>
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<FolderOpenIcon />}
          onClick={handleOpenPicker}
          disabled={disabled || siteOptions.length === 0 || ensuringSelection}
          loading={ensuringSelection}
        >
          {selectedFolders.length > 0 ? "Change folders" : "Select folders"}
        </Button>
      </Stack>

      {selectedFolders.length > 0 && (
        <Stack spacing={0.75}>
          {selectedFolders.map((folder) => (
            <Chip
              key={`${folder.drive_item_id || folder.path}-${folder.path}`}
              size="sm"
              variant="soft"
              color="primary"
            >
              {folder.path}
            </Chip>
          ))}
        </Stack>
      )}

      {activeSelection && (
        <SharepointFolderPicker
          open={pickerOpen}
          workspaceId={context?.workspaceId || ""}
          sourceName=""
          siteUrl={siteUrl}
          tenantId=""
          clientId=""
          selectionId={activeSelection.id}
          initialSelections={selectedFolders}
          onClose={() => setPickerOpen(false)}
          onConfirm={(selections) => {
            onChange(setSharepointValue(value, siteUrl, selections));
            setPickerOpen(false);
          }}
        />
      )}
    </Stack>
  );
};

const EmailSendersAndTemplatesWidget: React.FC<IntegrationWidgetProps> = ({
  context,
}) => {
  const workspaceId = context?.workspaceId;
  const organizationId = context?.organizationId;
  const enabledModules = context?.enabledModules || [];
  const moduleFilter = enabledModules[0];
  const body = useMemo(() => {
    if (!workspaceId || !organizationId) {
      return (
        <Alert color="warning" variant="soft">
          Workspace email settings are unavailable until the workspace context
          finishes loading.
        </Alert>
      );
    }

    return (
      <Tabs defaultValue={0}>
        <TabList disableUnderline>
          <Tab indicatorInset value={0}>
            Sender Addresses
          </Tab>
          <Tab indicatorInset value={1}>
            Templates
          </Tab>
        </TabList>
        <TabPanel value={0} sx={{ px: 0, pt: 2 }}>
          <WorkspaceEmailSenders workspaceId={workspaceId} />
        </TabPanel>
        <TabPanel value={1} sx={{ px: 0, pt: 2 }}>
          {moduleFilter ? (
            <WorkspaceEmailTemplates
              organizationId={organizationId}
              workspaceId={workspaceId}
              moduleFilter={moduleFilter}
            />
          ) : (
            <Alert color="neutral" variant="soft">
              Enable a workspace module before configuring workspace email
              templates.
            </Alert>
          )}
        </TabPanel>
      </Tabs>
    );
  }, [moduleFilter, organizationId, workspaceId]);

  if (context?.emailEnabled === false) {
    return (
      <DisabledEmailOverlay
        reason="Email is disabled for this organization. Workspace email settings here are inactive until email is re-enabled at the organization level."
        contextLink={{
          label: "Manage organization email",
          href: "/organization/settings",
        }}
      >
        {body}
      </DisabledEmailOverlay>
    );
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <EmailIcon sx={{ fontSize: 18, color: "primary.500" }} />
        <Typography level="body-sm">
          Manage workspace sender addresses and per-module email templates here.
        </Typography>
      </Stack>
      {body}
    </Stack>
  );
};

const widgetRegistry: Record<string, React.FC<IntegrationWidgetProps>> = {
  sharepoint_site_folder_picker: SharePointSiteFolderPickerWidget,
  email_senders_and_templates: EmailSendersAndTemplatesWidget,
};

export const getIntegrationWidget = (widgetName?: string | null) =>
  widgetName ? widgetRegistry[widgetName] : undefined;
